/**
 * El cielo y la Luna. Procedural, sin un byte de imagen descargada.
 *
 * Dos cosas que cambian mucho como se ve el juego por muy poco coste:
 *
 * **1. El cielo es un degradado, no un color.** Un color plano de fondo se lee
 * como "no han hecho el cielo"; un degradado da profundidad gratis y hace que
 * la pista nazca de algun sitio en vez de aparecer de la nada. Se pinta con una
 * esfera enorme vista desde dentro y un sombreador de doce lineas: no tiene
 * iluminacion, no tiene texturas y se dibuja una vez por cuadro.
 *
 * **2. La Luna se ve al fondo.** El juego se llama MoonRush y hasta ahora la
 * Luna no salia por ningun lado. Tener el destino a la vista le da sentido a
 * correr hacia delante: no es un pasillo infinito, es un viaje hacia algo.
 * Ademas es el punto de fuga de la composicion, que es lo que hace que una
 * captura de pantalla se entienda de un vistazo.
 *
 * Las dos cosas van con `fog: false` y `depthWrite: false`: son fondo, no
 * tienen que recibir niebla ni taparse con nada.
 */

import {
  AdditiveBlending,
  BackSide,
  CanvasTexture,
  Color,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  ShaderMaterial,
  SphereGeometry,
  Texture,
} from 'three';
import { mezclarColor } from './paleta';

// ---------------------------------------------------------------------------
// El cielo
// ---------------------------------------------------------------------------

const VERTEX = `
  varying vec3 vPos;
  void main() {
    vPos = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// El degradado va por altura. El exponente curva la mezcla: por encima de 1 el
// color del horizonte sube mas, por debajo se queda pegado al suelo.
//
// Esta en 1.6 despues de mirarlo: con 0.55 el horizonte se quedaba tan abajo
// que los edificios eran negro sobre negro y la ciudad no se leia. Un bioma de
// noche necesita que el resplandor suba lo suficiente para que las siluetas se
// recorten contra el; eso es lo que hace que parezca una ciudad y no un vacio.
const FRAGMENT = `
  uniform vec3 cenit;
  uniform vec3 horizonte;
  uniform float curva;
  varying vec3 vPos;
  void main() {
    float h = normalize(vPos).y;
    float t = pow(clamp(h * 0.5 + 0.5, 0.0, 1.0), curva);
    gl_FragColor = vec4(mix(horizonte, cenit, t), 1.0);
  }
`;

export interface Cielo {
  malla: Mesh;
  /** Cambia los colores y la curva del degradado. Lo llama el cambio de bioma. */
  pintar(cenit: number, horizonte: number, curva: number): void;
}

export function crearCielo(): Cielo {
  const material = new ShaderMaterial({
    uniforms: {
      cenit: { value: new Color(0x070a1e) },
      horizonte: { value: new Color(0x4a1a5c) },
      curva: { value: 1.6 },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    side: BackSide,
    depthWrite: false,
    fog: false,
  });

  // Pocos segmentos: es un degradado suave, no hace falta una esfera fina. El
  // radio tiene que caber dentro del `far` de la camara (260).
  const malla = new Mesh(new SphereGeometry(240, 16, 10), material);
  malla.renderOrder = -2;

  return {
    malla,
    pintar(cenit: number, horizonte: number, curva: number): void {
      (material.uniforms.cenit!.value as Color).setHex(cenit);
      (material.uniforms.horizonte!.value as Color).setHex(horizonte);
      material.uniforms.curva!.value = curva;
    },
  };
}

// ---------------------------------------------------------------------------
// Texturas procedurales
// ---------------------------------------------------------------------------

/**
 * Un disco con degradado radial, dibujado en un canvas.
 *
 * Es la pieza con la que se hace todo lo que brilla: la Luna, el halo de las
 * monedas y la estela del cohete. Se genera una vez y se comparte.
 *
 * 128 pixeles bastan: siempre se ve desenfocado por definicion, asi que mas
 * resolucion seria memoria de video tirada.
 */
export function texturaBrillo(dureza = 0.0): Texture {
  const lado = 128;
  const lienzo = document.createElement('canvas');
  lienzo.width = lado;
  lienzo.height = lado;
  const ctx = lienzo.getContext('2d')!;

  const g = ctx.createRadialGradient(lado / 2, lado / 2, 0, lado / 2, lado / 2, lado / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(Math.max(0.01, dureza), 'rgba(255,255,255,1)');
  g.addColorStop(Math.max(0.02, dureza + 0.12), 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, lado, lado);

  const t = new CanvasTexture(lienzo);
  t.needsUpdate = true;
  return t;
}

let brilloCompartido: Texture | null = null;
let discoCompartido: Texture | null = null;

/** El halo suave. Para estelas y auras. */
export function brillo(): Texture {
  if (!brilloCompartido) brilloCompartido = texturaBrillo(0.0);
  return brilloCompartido;
}

/** Un disco con borde definido y halo alrededor. Para la Luna. */
export function disco(): Texture {
  if (!discoCompartido) discoCompartido = texturaBrillo(0.42);
  return discoCompartido;
}

// ---------------------------------------------------------------------------
// La Luna
// ---------------------------------------------------------------------------

export interface Luna {
  malla: Mesh;
  /** Cambia el color y lo visible que esta. */
  pintar(color: number, opacidad: number): void;
}

/**
 * La Luna al fondo de la pista.
 *
 * Es un plano siempre de frente a la camara (no hace falta que gire: la camara
 * mira casi recto hacia -Z y un plano fijo se ve igual que una esfera a esa
 * distancia, por una fraccion del coste).
 *
 * Va con mezcla aditiva: en vez de taparse con el cielo, se SUMA, que es como
 * se comporta algo que emite luz. Un disco opaco sobre un degradado se ve
 * pegado; uno aditivo se ve encendido.
 */
export function crearLuna(): Luna {
  const grupo = new Mesh(); // contenedor: no se dibuja, solo agrupa

  // El halo, detras y grande. Es lo que da la sensacion de que emite luz.
  const matHalo = new MeshBasicMaterial({
    map: brillo(),
    color: 0xffffff,
    transparent: true,
    opacity: 0.45,
    blending: AdditiveBlending,
    depthWrite: false,
    fog: false,
  });
  const halo = new Mesh(new PlaneGeometry(110, 110), matHalo);
  halo.renderOrder = -1;
  grupo.add(halo);

  // El disco, delante y pequeno, con el borde definido.
  //
  // Son dos piezas y no una porque con una sola no sale: si el degradado es
  // suave se ve una mancha de niebla, y si es duro se ve una pegatina. La Luna
  // de verdad es las dos cosas a la vez, un disco nitido dentro de un
  // resplandor.
  const matDisco = new MeshBasicMaterial({
    map: disco(),
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    blending: AdditiveBlending,
    depthWrite: false,
    fog: false,
  });
  const cuerpo = new Mesh(new PlaneGeometry(34, 34), matDisco);
  cuerpo.renderOrder = -1;
  grupo.add(cuerpo);

  // Lejos y un poco por encima del horizonte: a la altura a la que el ojo
  // busca el destino.
  grupo.position.set(0, 30, -215);

  return {
    malla: grupo,
    pintar(color: number, opacidad: number): void {
      // El DISCO se mantiene casi blanco siempre. Pintarlo del color del
      // horizonte lo hacia desaparecer: con mezcla aditiva, un color oscuro
      // suma poco y la Luna se quedaba en una mancha. Lo que se tine es el
      // halo, que es lo que debe pertenecer al cielo.
      matDisco.color.setHex(mezclarColor(color, 0xfff4e0, 0.82));
      matDisco.opacity = opacidad;
      matHalo.color.setHex(color);
      matHalo.opacity = opacidad * 0.55;
    },
  };
}
